// ============================================================
// 티켓 투 라이드 SignalR 클라 wrapper
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §4-2
//   카탄 hub/catanHub.ts 패턴 일관 (connection 단일·자동 reconnect·invoke 헬퍼)
//   Phase 0 — 연결/invoke 6 + broadcast 핸들러 등록 stub. 본격 결선은 Phase 1+.
// ============================================================

import * as signalR from '@microsoft/signalr'
import {
  ticketStore,
  TtrGameStartedPayload,
  TtrTicketsDealtPayload,
  TtrTicketsDealtCallerPayload,
  TtrTurnStartedPayload,
  TtrCardDrawnPayload,
  TtrCardDrawnCallerPayload,
  TtrRouteClaimedPayload,
  TtrScoreUpdatedPayload,
  TtrTicketsDrawnPayload,
  TtrTicketsDrawnCallerPayload,
  TtrTicketsKeptPayload,
  TtrTrainRemainUpdatedPayload,
  TtrFaceUpRefilledPayload,
  TtrFinalTurnTriggeredPayload,
  TtrGameEndedPayload,
  TtrActionErrorPayload,
} from '../store/ticket'

const BASE_URL = process.env.REACT_APP_API_URL ?? ''

let connection: signalR.HubConnection | null = null
let lastToken:  string | null = null

// ── 연결 생성 / 반환 ──────────────────────────────────────
export function getConnection(token: string): signalR.HubConnection {
  lastToken = token
  if (connection && connection.state !== signalR.HubConnectionState.Disconnected) {
    return connection
  }
  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${BASE_URL}/Hub/TicketToRide?access_token=${encodeURIComponent(token)}`)
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build()
  return connection
}

// ── 연결 시작 / 보장 / 종료 ───────────────────────────────
export async function startConnection(token: string): Promise<void> {
  lastToken = token
  const conn = getConnection(token)
  if (conn.state === signalR.HubConnectionState.Disconnected) {
    await conn.start()
    registerBroadcastHandlers(conn)
  }
}

async function ensureConnected(): Promise<signalR.HubConnection> {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    if (!lastToken) throw new Error('TicketToRideHub 인증 토큰 없음 — 재로그인 필요')
    await startConnection(lastToken)
  }
  return connection!
}

export async function stopConnection(): Promise<void> {
  if (connection) {
    await connection.stop()
    connection = null
  }
}

export function isConnected(): boolean {
  return connection?.state === signalR.HubConnectionState.Connected
}

export function getState(): signalR.HubConnectionState | null {
  return connection?.state ?? null
}

// ── invoke 헬퍼 ──────────────────────────────────────────
async function invoke(method: string, ...args: unknown[]): Promise<unknown> {
  const conn = await ensureConnected()
  return conn.invoke(method, ...args)
}

// ════════════════════════════════════════════════════════
// 클라 → 서버 invoke 6종 (SoT §4-2)
// ════════════════════════════════════════════════════════

/** 방장 게임 시작 트리거 */
export async function requestGameStart(roomCode: string): Promise<void> {
  await invoke('RequestGameStart', roomCode)
}

/** 기차 카드 1장 드로우 (slotIndex 0~4=face-up / -1=비공개 덱) */
export async function drawTrainCard(matchId: number, slotIndex: number): Promise<void> {
  await invoke('DrawTrainCard', matchId, slotIndex)
}

/** 노선 점유 */
export async function claimRoute(
  matchId:   number,
  routeId:   number,
  laneNo:    number,
  colorUsed: string,
  wildCount: number,
): Promise<void> {
  await invoke('ClaimRoute', matchId, routeId, laneNo, colorUsed, wildCount)
}

/** 티켓 3장 임시 노출 (MAIN_TURN 한정) */
export async function drawTickets(matchId: number): Promise<void> {
  await invoke('DrawTickets', matchId)
}

/** 티켓 keep 결정 (SETUP 2~3 / MAIN 1~3) */
export async function keepTickets(matchId: number, keepTicketIdsCsv: string): Promise<void> {
  await invoke('KeepTickets', matchId, keepTicketIdsCsv)
}

/** 명시 턴 종료 */
export async function endTurn(matchId: number): Promise<void> {
  await invoke('EndTurn', matchId)
}

/**
 * 게임 상태 전체 로드 (재연결 / 진입 시점).
 * SEQ=10147 — Phase 0 운영 가동 ④ 보강. 결과는 GameLoadCaller 이벤트로 caller 본인만 수신.
 * 페이로드: { matchId, userCode, payloadJson } — payloadJson 은 sp_TtrGameLoad 10 결과셋 JSON wrapper.
 */
export async function gameLoad(matchId: number): Promise<void> {
  await invoke('GameLoad', matchId)
}

// ════════════════════════════════════════════════════════
// 서버 → 클라 broadcast 핸들러 12종 + Caller 변형 3 + 오류 1
//   Phase 0 — store apply* 함수에 wire-up. 본문은 stub 결선 (Phase 1+ 실제 동작 검증).
// ════════════════════════════════════════════════════════

function registerBroadcastHandlers(conn: signalR.HubConnection): void {
  // 그룹 broadcast
  conn.on('TtrGameStarted', (p: TtrGameStartedPayload) => {
    // TODO Phase 1 — payload 직렬화 형식 확인 (C# 익명 객체 → camelCase 변환)
    ticketStore.applyGameStarted(p)
  })

  conn.on('TtrTicketsDealt', (p: TtrTicketsDealtPayload) => {
    ticketStore.applyTicketsDealt(p)
  })

  conn.on('TtrAllTicketsKept', () => {
    // 전원 keep 완료 — MAIN_TURN 진입 알림. 실제 turn은 TtrTurnStarted 가 추가 전달.
    // TODO Phase 1 — UI 토스트 또는 store 플래그
  })

  conn.on('TtrTurnStarted', (p: TtrTurnStartedPayload) => {
    ticketStore.applyTurnStarted(p)
  })

  conn.on('TtrCardDrawn', (p: TtrCardDrawnPayload) => {
    ticketStore.applyCardDrawn(p)
  })

  conn.on('TtrRouteClaimed', (p: TtrRouteClaimedPayload) => {
    ticketStore.applyRouteClaimed(p)
  })

  conn.on('TtrScoreUpdated', (p: TtrScoreUpdatedPayload) => {
    ticketStore.applyScoreUpdated(p)
  })

  conn.on('TtrTicketsDrawn', (p: TtrTicketsDrawnPayload) => {
    ticketStore.applyTicketsDrawn(p)
  })

  conn.on('TtrTicketsKept', (p: TtrTicketsKeptPayload) => {
    ticketStore.applyTicketsKept(p)
  })

  conn.on('TtrTrainRemainUpdated', (p: TtrTrainRemainUpdatedPayload) => {
    ticketStore.applyTrainRemainUpdated(p)
  })

  conn.on('TtrFaceUpRefilled', (p: TtrFaceUpRefilledPayload) => {
    ticketStore.applyFaceUpRefilled(p)
  })

  conn.on('TtrFinalTurnTriggered', (p: TtrFinalTurnTriggeredPayload) => {
    ticketStore.applyFinalTurnTriggered(p)
  })

  conn.on('TtrGameEnded', (p: TtrGameEndedPayload) => {
    ticketStore.applyGameEnded(p)
  })

  // Clients.Caller 전용 (본인 한정 실데이터)
  conn.on('TicketsDealtCaller', (p: TtrTicketsDealtCallerPayload) => {
    ticketStore.applyTicketsDealtCaller(p)
  })

  conn.on('CardDrawnCaller', (p: TtrCardDrawnCallerPayload) => {
    ticketStore.applyCardDrawnCaller(p)
  })

  conn.on('TicketsDrawnCaller', (p: TtrTicketsDrawnCallerPayload) => {
    ticketStore.applyTicketsDrawnCaller(p)
  })

  // SEQ=10147 — GameLoadCaller (Hub.GameLoad invoke 응답)
  //   payloadJson 은 sp_TtrGameLoad 10 결과셋 JSON wrapper. ticketStore.applyGameState 가 매핑.
  conn.on('GameLoadCaller', (p: { matchId: number; userCode: number; payloadJson: string }) => {
    try {
      const parsed = JSON.parse(p.payloadJson) as Record<string, unknown>
      ticketStore.applyGameState(parsed)
    } catch (e) {
      console.error('[TicketToRideHub] GameLoadCaller payload parse 실패', e)
    }
  })

  // 오류 (Caller 전용) — 외부 listener 큐 fan-out (PlayTicket가 토스트 처리)
  conn.on('TtrActionError', (p: TtrActionErrorPayload) => {
    console.warn('[TicketToRideHub] TtrActionError', p)
    _errorListeners.forEach(fn => {
      try { fn(p) } catch (e) { console.error('[TtrActionError listener]', e) }
    })
  })

  // 강제 로그아웃
  conn.on('ForceLogout', (msg: string) => {
    console.warn('[TicketToRideHub] ForceLogout', msg)
    window.dispatchEvent(new CustomEvent('session-expired'))
  })
}

// ── 이벤트 리스너 등록 헬퍼 (외부 컴포넌트가 직접 등록 시) ────
type HubCallback = (...args: unknown[]) => void

export function on(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.on(event, callback)
}

export function off(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.off(event, callback)
}

// ── ActionError 외부 listener 큐 (connection 미준비 상태에서도 등록 안전) ──
type ErrorListener = (p: TtrActionErrorPayload) => void
const _errorListeners: Set<ErrorListener> = new Set()

export function onActionError(fn: ErrorListener): () => void {
  _errorListeners.add(fn)
  return () => { _errorListeners.delete(fn) }
}
